/**
 * CMS 485 Tab Component for Client Detail Page
 * Displays CMS 485 forms with signatures and actions
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FilePlus, PenTool, Link, Send, Download, Edit, AlertTriangle, RotateCw } from "lucide-react";

export function CMS485Tab({
  cms485Forms,
  onNewForm,
  onEditForm,
  onSignForm,
  onSendSignLink,
  onFaxForm,
  onQuickRefax,
  onDownloadPdf
}) {
  const [editConfirmOpen, setEditConfirmOpen] = useState(false);
  const [formToEdit, setFormToEdit] = useState(null);

  // Check if form can be edited (not fully signed)
  const canEdit = (form) => {
    return form.status !== 'signed';
  };

  // Check if form has any signatures that would be cleared
  const hasPartialSignatures = (form) => {
    return form.agency_signature || form.physician_signature;
  };

  // Handle edit button click
  const handleEditClick = (form) => {
    if (hasPartialSignatures(form)) {
      // Show confirmation dialog if there are signatures
      setFormToEdit(form);
      setEditConfirmOpen(true);
    } else {
      // Direct edit if no signatures
      onEditForm(form);
    }
  };

  // Confirm edit and clear signatures
  const confirmEdit = () => {
    if (formToEdit) {
      onEditForm(formToEdit, true); // Pass flag to clear signatures
    }
    setEditConfirmOpen(false);
    setFormToEdit(null);
  };

  return (
    <div data-testid="client-cms485-tab">
      <div className="form-section">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">CMS 485 Forms</h3>
            <p className="text-sm text-slate-500">Home Health Certification and Plan of Care</p>
          </div>
          <Button
            size="sm"
            onClick={onNewForm}
            className="gap-2"
            data-testid="new-cms485-btn"
          >
            <FilePlus className="w-4 h-4" />
            New CMS 485
          </Button>
        </div>
        
        {cms485Forms.length === 0 ? (
          <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
            <FilePlus className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No CMS 485 forms created</p>
            <p className="text-xs mt-1">Click &quot;New CMS 485&quot; to create a Home Health Certification form</p>
          </div>
        ) : (
          <div className="space-y-4">
            {cms485Forms.map((form) => (
              <div
                key={form.id}
                className="p-4 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors"
                data-testid={`cms485-card-${form.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-slate-800">
                      Certification Period: {form.certification_period_from} to {form.certification_period_to}
                    </h4>
                    <p className="text-sm text-slate-500 mt-1">
                      Primary Diagnosis: {form.principal_diagnosis || "Not specified"}
                    </p>
                    {form.agency_rn_name && (
                      <p className="text-sm text-slate-500">
                        Agency RN: {form.agency_rn_name}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Enhanced Edit Button - Always visible unless fully signed */}
                    {canEdit(form) && (
                      <Button
                        size="sm"
                        variant={hasPartialSignatures(form) ? "outline" : "default"}
                        className={`gap-2 ${
                          hasPartialSignatures(form) 
                            ? "border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800" 
                            : "bg-blue-600 hover:bg-blue-700 text-white"
                        }`}
                        onClick={() => handleEditClick(form)}
                        data-testid={`edit-cms485-${form.id}`}
                      >
                        <Edit className="w-4 h-4" />
                        {hasPartialSignatures(form) ? "Edit (Clear Signatures)" : "Edit Form"}
                      </Button>
                    )}
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      form.status === 'signed' 
                        ? 'bg-emerald-100 text-emerald-700'
                        : form.status === 'pending_signature'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-slate-100 text-slate-600'
                    }`}>
                      {form.status === 'signed' ? 'Fully Signed' : form.status === 'pending_signature' ? 'Pending Signature' : 'Draft'}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-slate-500">Physician</p>
                    <p className="text-slate-700">{form.physician_name || "Not specified"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Prognosis</p>
                    <p className="text-slate-700 capitalize">{form.prognosis || "Not specified"}</p>
                  </div>
                </div>
                
                {/* Signature Status */}
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-xs text-slate-500">Agency Signature</p>
                      {form.agency_signature ? (
                        <div data-testid="cms485-agency-signature">
                          {String(form.agency_signature).startsWith("data:image") && (
                            <img src={form.agency_signature} alt="Nurse signature" className="h-10 max-w-[180px] object-contain border-b border-slate-300" />
                          )}
                          <p className="text-emerald-600 font-medium text-xs mt-0.5">
                            ✓ Signed by {form.signed_by_agency}{form.agency_signature_date ? ` · ${form.agency_signature_date.split('T')[0]}` : ''}
                          </p>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-1 gap-1"
                          onClick={() => onSignForm(form.id, "agency")}
                        >
                          <PenTool className="w-3 h-3" />
                          Sign
                        </Button>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Physician Signature</p>
                      {form.physician_signature ? (
                        <div data-testid="cms485-physician-signature">
                          {String(form.physician_signature).startsWith("data:image") && (
                            <img src={form.physician_signature} alt="Physician signature" className="h-10 max-w-[180px] object-contain border-b border-slate-300" />
                          )}
                          <p className="text-emerald-600 font-medium text-xs mt-0.5">
                            ✓ Signed by {form.signed_by_physician}{form.physician_signature_date ? ` · ${form.physician_signature_date.split('T')[0]}` : ''}
                          </p>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-1 gap-1"
                          onClick={() => onSignForm(form.id, "physician")}
                        >
                          <PenTool className="w-3 h-3" />
                          Sign
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                  <p className="text-xs text-slate-400">
                    Created: {form.created_at?.split('T')[0] || 'N/A'}
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    {!form.physician_signature && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1 text-xs"
                        onClick={() => onSendSignLink("cms485", form.id, "physician")}
                        data-testid={`sign-link-cms485-${form.id}`}
                        title="Send a secure link to the physician to sign"
                      >
                        <Link className="w-3 h-3" />
                        Physician Sign Link
                      </Button>
                    )}
                    {!form.agency_signature && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1 text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                        onClick={() => onSendSignLink("cms485", form.id, "nurse")}
                        data-testid={`nurse-sign-link-cms485-${form.id}`}
                        title="Send a secure link to the agency nurse to sign"
                      >
                        <Link className="w-3 h-3" />
                        Nurse Sign Link
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1 text-xs"
                      onClick={() => onFaxForm("cms485", form.id, form.physician_name)}
                      data-testid={`fax-cms485-${form.id}`}
                    >
                      <Send className="w-3 h-3" />
                      eFax
                    </Button>
                    {onQuickRefax && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1 text-xs border-teal-200 text-teal-700 hover:bg-teal-50"
                        onClick={() => onQuickRefax("cms485", form.id)}
                        data-testid={`refax-cms485-${form.id}`}
                        title="Re-fax to the last recipient (one click)"
                      >
                        <RotateCw className="w-3 h-3" />
                        Re-fax
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1"
                      onClick={() => onDownloadPdf(form.id)}
                      data-testid={`download-cms485-${form.id}`}
                    >
                      <Download className="w-4 h-4" />
                      PDF
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Confirmation Dialog for Partially Signed Forms */}
      <AlertDialog open={editConfirmOpen} onOpenChange={setEditConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Edit Signed Form?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>This form has existing signatures that will be cleared if you edit:</p>
                {formToEdit && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
                    {formToEdit.agency_signature && (
                      <p className="text-sm text-amber-800">
                        • Agency signature by <strong>{formToEdit.signed_by_agency}</strong> will be removed
                      </p>
                    )}
                    {formToEdit.physician_signature && (
                      <p className="text-sm text-amber-800">
                        • Physician signature by <strong>{formToEdit.signed_by_physician}</strong> will be removed
                      </p>
                    )}
                  </div>
                )}
                <p className="text-sm text-slate-600">
                  After editing, the form will need to be re-signed. This action cannot be undone.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-amber-600 hover:bg-amber-700"
              onClick={confirmEdit}
            >
              Edit & Clear Signatures
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
